import { OverlayContainer } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, HostBinding, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MediaChange, MediaObserver } from "@ngbracket/ngx-layout";
import { MatSidenav, MatSidenavContainer } from "@angular/material/sidenav";
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthenticationService, LoginUser } from '@mango/authentication';
import { AuthorizationScheme, AuthorizationSchemeService, GlobalService, CollapsibleNavListComponent } from '@mango/core';
import { Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AlertService } from 'src/app/alert/alert.service';
import { Location, AsyncPipe } from '@angular/common';
import { AuthorizationGroupService } from '@mango/authorization';
import { MatToolbar } from '@angular/material/toolbar';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatBadge } from '@angular/material/badge';
import { AlertsPopupComponent } from '../alert-popup/alert-popup.component';
import { MatNavList, MatListItem } from '@angular/material/list';
import { ItemTileComponent } from '../item-tile/itemtile.component';


@Component({
    selector: 'base-layout',
    templateUrl: './base-layout.component.html',
    styleUrls: ['./base-layout.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatToolbar, MatProgressBar, MatIconButton, MatIcon, MatTooltip, MatMenuTrigger, MatBadge, MatMenu, AlertsPopupComponent, MatMenuItem, RouterLink, MatSidenavContainer, MatSidenav, MatNavList, MatListItem, RouterLinkActive, CollapsibleNavListComponent, RouterOutlet, ItemTileComponent, AsyncPipe]
})
export class BaseLayoutComponent implements OnInit, OnDestroy {

    @ViewChild('sidenav', { static: true }) sidenav: MatSidenav;
    @HostBinding('class') componentCssClass;
    myScheme: AuthorizationScheme;
    watcher: Subscription;
    mode: 'side' | 'over' = 'side';
    opened: boolean = true;

    protected onDestroy$ = new Subject<void>();
    amountOfNotifications: number;


    constructor(private media: MediaObserver,
        public global: GlobalService,
        private authService: AuthorizationSchemeService,
        private authenticationService: AuthenticationService,
        private alertService: AlertService,
        private overlayContainer: OverlayContainer,
        private cd: ChangeDetectorRef,
        private snackbar: MatSnackBar,
        private router: Router,
        private location: Location,
        private groupService: AuthorizationGroupService
    ) {
        this.watcher = media.asObservable().pipe(map(changes => changes[0])).subscribe((change: MediaChange) => {
            if (change.mqAlias == 'xs' || change.mqAlias == 'sm') {
                this.mode = 'over'
                this.opened = false;
            } else {
                this.mode = 'side';
                this.opened = true;
            }
        });
    }

    async ngOnInit() {


        this.myScheme = this.authService.getMyScheme();
        const groupId = this.authenticationService.currentUser['auth_groups'];

        if (!this.myScheme) {
            this.router.navigateByUrl('/login');
        }

        this.getAmountOfNotifications();


        var theme = localStorage.getItem('theme') || 'light-theme';
        this.setTheme(theme);
        this.eeActive = localStorage.getItem('easter-egg') ? true : false;

        this.alertService.hubSubject.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
            // a notification for this user, or a change that concerns everyone (a removed or changed alert)
            if (data == null || data == +this.authenticationService.currentUser.id) {
                this.getAmountOfNotifications();
            }
        });


        // When the user is set to role "VisitorAccount" it should only be allowed to go to the landing page, and should be forced to the landingpage.
        // Therefor we need to push this person to the page,
        if(groupId) {
            await this.groupService.getById(+groupId, [x => x.name]).subscribe((value) => {
                var group = value;

                if(group.name === "VisitorAccount") {
                    this.router.navigate(['/landing']);
                }

            });
        }

    }

    ngOnDestroy(): void {
        if (this.watcher) {
            this.watcher.unsubscribe();
        }

        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    closeSideNav() {
        if (this.media.isActive('lt-md')) {
            this.sidenav.close();
        }
    }



    setTheme(theme: string) {
        const currentTheme = localStorage.getItem('theme') || 'light-theme';
        const overlayClassList = this.overlayContainer.getContainerElement().classList;
        if (overlayClassList.contains(currentTheme)) {
            overlayClassList.remove(currentTheme);
        }
        overlayClassList.add(theme);
        this.componentCssClass = theme;
        localStorage.setItem('theme', theme);
    }

    stopPropagation(ev) {
        ev.stopPropagation();
        ev.preventDefault();
        return false;
    }

    canRead(type: string) {
        return this.myScheme.canRead(type);
    }

    get currentUser(): LoginUser {
        return this.authenticationService.currentUser;
    }

    getAmountOfNotifications() {
        if (this.myScheme.canRead('AccessMonitor.Logic.Alert')) {
            this.alertService.get([x => x.count(x.id)]).subscribe(x => {
                this.amountOfNotifications = x.data.length > 0 ? (x.data[0].count_Id ?? x.data[0].Count_Id ?? 0) : 0; // Mango 22 starts the name of an aggregate with a small letter
            });
        }
    }

    // An inconspicuous easter-egg
    eeActive = false;
    private eeClickCount = 0;
    onEasterEggClicked(event: any) {
        event.preventDefault(); event.stopPropagation();
        this.eeClickCount++;
        if (this.eeClickCount == 7) { this.snackbar.open('Wat? Deze knop? Nee die doet niks.', 'OK', { duration: 3500 }); }
        if (this.eeClickCount == 14) { this.snackbar.open(`Jij bent wel een nieuwsgierig aagje, of niet?`, 'OK', { duration: 3500 }); }
        if (this.eeClickCount == 21) { this.snackbar.open('Oké, Misschien gebeurd er toch wat..', 'OK', { duration: 3500 }); }
        if (this.eeClickCount == 28) { this.eeActive = true; localStorage.setItem('easter-egg', '1'); }
    }
    onThemesClosed(event: any) { this.eeClickCount = 0; }
}


